import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, CardLayout, InputField, SelectField } from "../v3";
import { CompanyService } from "../../services";
import { companyValidationRules } from "../../pages/superadmin/validation";
import { formatPayload } from "../../utils";
import { markTableReset } from "../../utils/tableStateStorage";
import { GST_STATE_MAP } from "../../constants/gstStateMap";

const stateOptions = Object.entries(GST_STATE_MAP).map(([id, name]) => ({
  id,
  name,
}));

const getStateOptions = async ({ name = "" } = {}) => ({
  data: stateOptions.filter((option) => option.name.toLowerCase().includes(name.toLowerCase())),
});

export const AddEditCompanyForm = ({ companyId }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({
    name: undefined,
    primaryPhone: undefined,
    primaryEmail: undefined,
    ownershipType: undefined,
    parentCompanyId: undefined,
    primaryAddress: {
      locality: undefined,
      state: "",
      stateCode: "",
      pinCode: undefined,
      country: undefined,
    },
    taxDetails: {
      gstNumber: undefined,
      panNumber: undefined,
    },
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (companyId) fetchData(companyId);
  }, [companyId]);

  const fetchData = async (id) => {
    try {
      const { data: company } = await CompanyService.getCompanyDetails(id);
      setCompanyDetails({
        name: company?.name,
        primaryPhone: company?.primaryPhone,
        primaryEmail: company?.primaryEmail,
        ownershipType: company?.ownershipType,
        primaryAddress: {
          locality: company?.primaryAddress?.locality,
          state: company?.primaryAddress?.state,
          stateCode: company?.primaryAddress?.stateCode,
          pinCode: company?.primaryAddress?.pinCode,
          country: company?.primaryAddress?.country,
        },
        taxDetails: {
          gstNumber: company?.taxDetails?.gstNumber,
          panNumber: company?.taxDetails?.panNumber,
        },
      });
      setSelectedCompany(company?.parentCompany || null);
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
      toast.error("Failed to fetch company details");
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;

    if (name === "gstNumber") {
      setCompanyDetails((prev) => ({
        ...prev,
        taxDetails: {
          ...prev.taxDetails,
          gstNumber: String(value).toUpperCase(),
        },
      }));

      return;
    }

    if (["locality", "state", "stateCode", "pinCode", "country"].includes(name)) {
      setCompanyDetails((prev) => ({
        ...prev,
        primaryAddress: {
          ...prev.primaryAddress,
          [name]: value,
        },
      }));
    } else if (["panNumber"].includes(name)) {
      setCompanyDetails((prev) => ({
        ...prev,
        taxDetails: {
          ...prev.taxDetails,
          [name]: value,
        },
      }));
    } else {
      setCompanyDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  const handleStateChange = (selectedState) => {
    setCompanyDetails((prev) => ({
      ...prev,
      primaryAddress: {
        ...prev.primaryAddress,
        state: selectedState?.name || "",
        stateCode: selectedState?.id || "",
      },
    }));
  };
  const handleCompanyChange = (selected) => {
    setSelectedCompany(selected);
    setCompanyDetails((prev) => ({
      ...prev,
      parentCompanyId: selected?.id,
    }));
  };

  const handleClearForm = () => {
    setErrors({});
    setCompanyDetails({
      name: undefined,
      primaryPhone: undefined,
      primaryEmail: undefined,
      ownershipType: undefined,
      parentCompanyId: undefined,
      primaryAddress: {
        locality: undefined,
        state: "",
        stateCode: "",
        pinCode: undefined,
        country: undefined,
      },
      taxDetails: {
        gstNumber: undefined,
        panNumber: undefined,
      },
    });
    navigate(-1);
  };

  const formatCompanyPayload = (details) => {
    const formatted = { ...details };
    const payload = formatPayload(formatted);

    if (payload.primaryAddress) {
      const address = payload.primaryAddress;
      const keys = ["locality", "state", "stateCode", "pinCode", "country"];
      const allEmpty = keys.every((key) => !address[key] || address[key].toString().trim() === "");

      if (allEmpty) {
        // all empty → set empty string for each key
        keys.forEach((key) => {
          address[key] = "";
        });
      } else {
        // some have value → fill missing with empty string
        keys.forEach((key) => {
          if (!address[key] || address[key].toString().trim() === "") {
            address[key] = "";
          }
        });
      }
    }

    return payload;
  };

  const handleSaveCompany = async () => {
    setErrors({});
    const formattedData = formatCompanyPayload(companyDetails);
    const validationErrors = companyValidationRules(formattedData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      if (companyId) await CompanyService.updateCompany(companyId, formattedData);
      else await CompanyService.createCompany(formattedData);
      toast.success(`Company ${companyId ? "updated" : "added"} successfully`);
      // A new company would be hidden by the filters the list had, so it opens fresh; after an edit they're kept.
      if (!companyId) markTableReset();
      handleClearForm();
    } catch (error) {
      toast.error(`Error ${companyId ? "updating" : "adding"} company`);
      console.error("🚀 ~ handleSaveCompany ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  const ownershipTypes = [
    { label: "SINGLE", value: "SINGLE" },
    { label: "PARENT", value: "PARENT" },
    { label: "CHILD", value: "CHILD" },
  ];
  return (
    <CardLayout>
      <Box className="fl gap-2">
        <InputField
          required
          label="Company Name"
          name="name"
          value={companyDetails.name}
          onChange={updateDetailsState}
          error={!!errors.name}
          helperText={errors.name}
          style={{ width: "100%" }}
        />
        <InputField
          required
          type="tel"
          label="Mobile"
          name="primaryPhone"
          value={companyDetails.primaryPhone}
          onChange={updateDetailsState}
          error={!!errors.primaryPhone}
          helperText={errors.primaryPhone}
          style={{ width: "100%" }}
        />
        <InputField
          required
          type="email"
          label="Email"
          name="primaryEmail"
          value={companyDetails.primaryEmail}
          onChange={updateDetailsState}
          error={!!errors.primaryEmail}
          helperText={errors.primaryEmail}
          style={{ width: "100%" }}
        />
      </Box>
      <Box className="fl gap-2">
        <InputField
          select={true}
          selectOptions={ownershipTypes}
          label="Ownership Type"
          name="ownershipType"
          value={companyDetails.ownershipType}
          onChange={updateDetailsState}
          disabled={!!companyId}
          error={!!errors.ownershipType}
          helperText={errors.ownershipType}
          style={{ width: "100%" }}
        />
        {companyDetails.ownershipType === "CHILD" && (
          <SelectField
            label="Company"
            value={selectedCompany}
            getData={CompanyService.getCompanyList}
            getDataStaticFilters={{ active: true, ownershipType: "PARENT", sortBy: "name", sortOrder: "asc" }}
            disabled={!!companyId}
            required
            error={!!errors.parentCompanyId}
            helperText={errors.parentCompanyId}
            onChange={handleCompanyChange}
            style={{ width: "100%" }}
          />
        )}
      </Box>
      <Box className="fl gap-2">
        <InputField
          label="GST Number"
          name="gstNumber"
          value={companyDetails?.taxDetails?.gstNumber}
          onChange={updateDetailsState}
          error={!!errors.taxDetails?.gstNumber}
          helperText={errors.taxDetails?.gstNumber}
          style={{ width: "100%" }}
        />
        <InputField
          label="PAN Number"
          name="panNumber"
          value={companyDetails?.taxDetails?.panNumber}
          onChange={updateDetailsState}
          error={!!errors.taxDetails?.panNumber}
          helperText={errors.taxDetails?.panNumber}
          style={{ width: "100%" }}
        />
      </Box>
      <Box className="fl gap-2">
        <InputField
          label="Locality"
          name="locality"
          value={companyDetails.primaryAddress.locality}
          onChange={updateDetailsState}
          style={{ width: "100%" }}
        />
        <SelectField
          label="State"
          value={stateOptions.find((option) => option.id === companyDetails.primaryAddress.stateCode) || null}
          getData={getStateOptions}
          onChange={handleStateChange}
          style={{ width: "100%" }}
        />
        <InputField label="State Code" value={companyDetails.primaryAddress.stateCode} disabled style={{ width: "100%" }} />
        <InputField
          label="Pin code"
          name="pinCode"
          type="number"
          value={companyDetails.primaryAddress.pinCode}
          onChange={updateDetailsState}
          error={!!errors?.primaryAddress?.pinCode}
          helperText={errors?.primaryAddress?.pinCode}
          style={{ width: "100%" }}
        />
        <InputField
          label="Country"
          name="country"
          value={companyDetails.primaryAddress.country}
          onChange={updateDetailsState}
          style={{ width: "100%" }}
        />
      </Box>
      <Box className={"d-flex justify-content-end gap-1"}>
        <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleClearForm} />
        <ActionButton buttonText="Submit" color="primary" disabled={loading} onButtonClick={handleSaveCompany} />
      </Box>
    </CardLayout>
  );
};
